import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { CalendarDays } from "lucide-react";

interface DatePickerProps {
  label?: string;
  name: string;
  value: string | null | undefined;
  onChange: (value: string) => void;
  required?: boolean;
  className?: string; // Dışarıdan class desteği eklendi
}

export const DatePicker: React.FC<DatePickerProps> = ({
  label,
  name,
  value,
  onChange,
  required = false,
  className = "", // Varsayılan boş string
}) => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(new Date());
  const [position, setPosition] = useState<"top" | "bottom">("bottom");
  const ref = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useLayoutEffect(() => {
    if (open && ref.current && popRef.current) {
      const inputRect = ref.current.getBoundingClientRect();
      const popupHeight = popRef.current.offsetHeight;
      const spaceBelow = window.innerHeight - inputRect.bottom;
      const spaceAbove = inputRect.top;
      setPosition(spaceBelow < popupHeight && spaceAbove > popupHeight ? "top" : "bottom");
    }
  }, [open]);

  const months = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
  const year = current.getFullYear();
  const month = current.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const daysArray = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const selectDate = (d: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    onChange(dateStr);
    setOpen(false);
  };

  return (
    /* Gelen className'i ana kapsayıcıya ekliyoruz. 
       Eğer className içinde genişlik (w-...) belirtilmemişse 
       varsayılan olarak w-full'ün çalışması için kontrol eklendi.
    */
    <div 
      className={`relative ${!className.includes('w-') ? 'w-full' : ''} ${className}`} 
      ref={ref}
    >
      {label && (
        <label htmlFor={name} className="block text-sm font-medium mb-1">
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}

      {/* Input - Mevcut 'input' class'ın korundu */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="input flex items-center justify-between cursor-pointer w-full"
      >
        <span>{value ? value : "Tarih Seç"}</span>
        <CalendarDays className="w-5 h-5 opacity-60" />
      </button>

      {/* POPUP CALENDAR - Mevcut 'card' ve 'animate-fadeIn' class'ların korundu */}
      {open && (
        <div
          ref={popRef}
          className={`
            absolute z-50 card shadow-xl p-4 w-72 animate-fadeIn
            ${position === "bottom" ? "mt-2 top-full" : "bottom-full mb-2"}
          `}
          style={{ left: 0 }}
        >
          <div className="flex justify-between items-center mb-3">
            <button
              type="button"
              className="p-1 hover:bg-muted rounded"
              onClick={() => setCurrent(new Date(year, month - 1, 1))}
            >
              ‹
            </button>
            <span className="font-medium">{months[month]} {year}</span>
            <button
              type="button"
              className="p-1 hover:bg-muted rounded"
              onClick={() => setCurrent(new Date(year, month + 1, 1))}
            >
              ›
            </button>
          </div>

          <div className="grid grid-cols-7 text-center text-xs text-muted mb-1">
            {["Pz", "Pt", "Sa", "Ça", "Pe", "Cu", "Ct"].map((d) => (
              <div key={d}>{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1 text-center">
            {daysArray.map((day, idx) =>
              day ? (
                <button
                  key={idx}
                  onClick={() => selectDate(day)}
                  className={`p-2 rounded-lg transition 
                    ${
                      value &&
                      new Date(value).toDateString() ===
                        new Date(year, month, day).toDateString()
                        ? "bg-primary text-white shadow"
                        : "hover:bg-muted"
                    }
                  `}
                >
                  {day}
                </button>
              ) : (
                <div key={idx} />
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};